// SPDX-License-Identifier: AGPL-3.0-or-later

import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import type {NativeAudioAvailability, NativeAudioStartOptions} from '@app/types/electron.d';

export const MAX_GENERATOR_PENDING_AUDIO_FRAMES = 64;
export const GENERATOR_AUDIO_CHUNK_DURATION_US = 20_000;
export const GENERATOR_AUDIO_PREBUFFER_US = 200_000;
export const GENERATOR_AUDIO_MAX_PREBUFFER_US = 1_000_000;
export const GENERATOR_AUDIO_PREBUFFER_STEP_US = 100_000;
export const GENERATOR_AUDIO_PREBUFFER_TIMEOUT_MS = 250;
export const GENERATOR_AUDIO_REBUFFER_GAP_MS = 160;
export const MAX_GENERATOR_BUFFERED_AUDIO_US = 1_500_000;
export const MIN_NATIVE_AUDIO_SAMPLE_RATE = 8000;
export const MAX_NATIVE_AUDIO_SAMPLE_RATE = 192000;
export const MAX_NATIVE_AUDIO_CHANNELS = 8;
export const MAX_NATIVE_AUDIO_FRAME_SECONDS = 1;

export type ArmedNativeAudioCapture =
	| {
			kind: 'capture';
			captureId: string;
			includeSelfWindowAudio?: boolean;
	  }
	| {
			kind: 'linux-routing';
			linuxRule: NonNullable<NativeAudioStartOptions['linuxRule']>;
			includeSelfWindowAudio?: boolean;
	  }
	| {
			kind: 'self-window-web-audio';
	  };

export interface NativeAudioBridgeHandle {
	track: MediaStreamTrack;
	cleanup: (stopRemote?: boolean) => Promise<void>;
}

export interface ActiveNativeAudioBridge {
	captureId: string;
	cleanup: (stopRemote?: boolean) => Promise<void>;
}

export interface GeneratorAudioTrack extends MediaStreamTrack {
	writable: WritableStream<unknown>;
}

export interface AudioDataCtor {
	new (init: {
		data: BufferSource;
		format: string;
		sampleRate: number;
		numberOfFrames: number;
		numberOfChannels: number;
		timestamp: number;
	}): AudioDataFrame;
}

export interface GeneratorCtor {
	new (options: {kind: 'audio'}): GeneratorAudioTrack;
}

export interface AudioDataFrame {
	close: () => void;
}

export interface NativeAudioBridgeStats {
	active: boolean;
	bridgeMode: 'generator' | 'script-processor' | null;
	captureId: string | null;
	startedAt: number | null;
	lastFrameAt: number | null;
	lastFrameTimestampUs: number | null;
	framesReceived: number;
	framesDropped: number;
	lateFrameCount: number;
	rebufferCount: number;
	maxFrameArrivalGapMs: number;
	maxFrameTimestampGapMs: number;
	maxPendingFrames: number;
	maxBufferedDurationMs: number;
	prebufferTargetMs: number | null;
	frameDurationMs: number | null;
	endReason: string | null;
	endDetail: string | null;
	endedAt: number | null;
}

export const initialBridgeStats: NativeAudioBridgeStats = {
	active: false,
	bridgeMode: null,
	captureId: null,
	startedAt: null,
	lastFrameAt: null,
	lastFrameTimestampUs: null,
	framesReceived: 0,
	framesDropped: 0,
	lateFrameCount: 0,
	rebufferCount: 0,
	maxFrameArrivalGapMs: 0,
	maxFrameTimestampGapMs: 0,
	maxPendingFrames: 0,
	maxBufferedDurationMs: 0,
	prebufferTargetMs: null,
	frameDurationMs: null,
	endReason: null,
	endDetail: null,
	endedAt: null,
};

export function isNativeAudioDesktopPlatform(): boolean {
	const electronApi = getElectronAPI();
	return (
		Boolean(electronApi) &&
		(electronApi?.platform === 'darwin' || electronApi?.platform === 'win32' || electronApi?.platform === 'linux')
	);
}

export function getGeneratorCtor(): GeneratorCtor | undefined {
	return (
		window as typeof window & {
			MediaStreamTrackGenerator?: GeneratorCtor;
		}
	).MediaStreamTrackGenerator;
}

export function getAudioDataCtor(): AudioDataCtor | undefined {
	return (
		window as typeof window & {
			AudioData?: AudioDataCtor;
		}
	).AudioData;
}

export function getNativeAudioApi() {
	return getElectronAPI()?.nativeAudio ?? null;
}

export function isArrayBuffer(value: unknown): value is ArrayBuffer {
	return value instanceof ArrayBuffer || Object.prototype.toString.call(value) === '[object ArrayBuffer]';
}

export function isValidAudioFrameMessage(message: {
	sampleRate: number;
	channels: number;
	timestampUs: number;
	samples: ArrayBuffer;
}): boolean {
	const sampleCount = isArrayBuffer(message.samples) ? message.samples.byteLength / Float32Array.BYTES_PER_ELEMENT : 0;
	const frameCount = message.channels > 0 ? sampleCount / message.channels : 0;
	return (
		isArrayBuffer(message.samples) &&
		Number.isFinite(message.sampleRate) &&
		message.sampleRate >= MIN_NATIVE_AUDIO_SAMPLE_RATE &&
		message.sampleRate <= MAX_NATIVE_AUDIO_SAMPLE_RATE &&
		Number.isSafeInteger(message.channels) &&
		message.channels > 0 &&
		message.channels <= MAX_NATIVE_AUDIO_CHANNELS &&
		Number.isFinite(message.timestampUs) &&
		message.samples.byteLength % Float32Array.BYTES_PER_ELEMENT === 0 &&
		sampleCount >= message.channels &&
		sampleCount % message.channels === 0 &&
		frameCount <= Math.ceil(message.sampleRate * MAX_NATIVE_AUDIO_FRAME_SECONDS)
	);
}

export interface NativeAudioChunkerOutput {
	samples: Float32Array<ArrayBuffer>;
	sampleRate: number;
	channels: number;
	timestampUs: number;
	numberOfFrames: number;
	durationUs: number;
}

interface PendingNativeAudioSamples {
	samples: Float32Array<ArrayBuffer>;
	offsetFrames: number;
	frameCount: number;
}

export class NativeAudioFrameChunker {
	private readonly targetChunkDurationUs: number;
	private sampleRate = 0;
	private channels = 0;
	private nextTimestampUs = 0;
	private queuedFrames = 0;
	private readonly pendingSamples: Array<PendingNativeAudioSamples> = [];

	constructor(targetChunkDurationUs: number = GENERATOR_AUDIO_CHUNK_DURATION_US) {
		this.targetChunkDurationUs = targetChunkDurationUs;
	}

	push(message: {
		sampleRate: number;
		channels: number;
		timestampUs: number;
		samples: ArrayBuffer;
	}): Array<NativeAudioChunkerOutput> {
		const samples = new Float32Array(message.samples, 0, message.samples.byteLength / Float32Array.BYTES_PER_ELEMENT);
		const frameCount = Math.floor(samples.length / message.channels);
		if (frameCount <= 0) return [];
		if (message.sampleRate !== this.sampleRate || message.channels !== this.channels) {
			this.reset(message.sampleRate, message.channels, message.timestampUs);
		}
		this.pendingSamples.push({
			samples: new Float32Array(samples),
			offsetFrames: 0,
			frameCount,
		});
		this.queuedFrames += frameCount;
		const targetFrames = this.targetFramesPerChunk();
		const outputs: Array<NativeAudioChunkerOutput> = [];
		while (this.queuedFrames >= targetFrames) {
			const out = this.readFrames(targetFrames);
			const timestampUs = this.nextTimestampUs;
			const durationUs = Math.round((targetFrames / this.sampleRate) * 1_000_000);
			this.nextTimestampUs += durationUs;
			outputs.push({
				samples: out,
				sampleRate: this.sampleRate,
				channels: this.channels,
				timestampUs,
				numberOfFrames: targetFrames,
				durationUs,
			});
		}
		return outputs;
	}

	private reset(sampleRate: number, channels: number, timestampUs: number): void {
		this.sampleRate = sampleRate;
		this.channels = channels;
		this.nextTimestampUs = Math.max(0, Math.round(timestampUs));
		this.queuedFrames = 0;
		this.pendingSamples.length = 0;
	}

	private targetFramesPerChunk(): number {
		return Math.max(1, Math.round((this.sampleRate * this.targetChunkDurationUs) / 1_000_000));
	}

	private readFrames(frameCount: number): Float32Array<ArrayBuffer> {
		const out = new Float32Array(frameCount * this.channels);
		let writtenFrames = 0;
		while (writtenFrames < frameCount && this.pendingSamples.length > 0) {
			const chunk = this.pendingSamples[0]!;
			const availableFrames = chunk.frameCount - chunk.offsetFrames;
			const framesToCopy = Math.min(frameCount - writtenFrames, availableFrames);
			const sourceStart = chunk.offsetFrames * this.channels;
			const sourceEnd = sourceStart + framesToCopy * this.channels;
			out.set(chunk.samples.subarray(sourceStart, sourceEnd), writtenFrames * this.channels);
			writtenFrames += framesToCopy;
			chunk.offsetFrames += framesToCopy;
			this.queuedFrames -= framesToCopy;
			if (chunk.offsetFrames >= chunk.frameCount) {
				this.pendingSamples.shift();
			}
		}
		return out;
	}
}

export async function replaceStreamAudioTrack(stream: MediaStream, nextTrack: MediaStreamTrack): Promise<void> {
	for (const existingAudioTrack of stream.getAudioTracks()) {
		stream.removeTrack(existingAudioTrack);
		try {
			existingAudioTrack.stop();
		} catch {}
	}
	stream.addTrack(nextTrack);
}

export function stopStreamTracks(stream: MediaStream): void {
	for (const track of stream.getTracks()) {
		try {
			track.stop();
		} catch {}
	}
}

export function restoreTrackStop(track: MediaStreamTrack, originalStop: MediaStreamTrack['stop']): void {
	try {
		Object.defineProperty(track, 'stop', {
			value: originalStop,
			configurable: true,
			writable: true,
		});
	} catch {
		try {
			(track as MediaStreamTrack & {stop: MediaStreamTrack['stop']}).stop = originalStop;
		} catch {}
	}
}

export function patchTrackStopForCleanup(track: MediaStreamTrack, cleanup: () => void): () => void {
	const originalStop = track.stop;
	let restored = false;
	const patchedStop = function patchedNativeAudioTrackStop(this: MediaStreamTrack): void {
		cleanup();
		originalStop.call(this);
	};
	try {
		Object.defineProperty(track, 'stop', {
			value: patchedStop,
			configurable: true,
			writable: true,
		});
	} catch {
		try {
			(track as MediaStreamTrack & {stop: MediaStreamTrack['stop']}).stop = patchedStop;
		} catch {
			return () => undefined;
		}
	}
	return () => {
		if (restored) return;
		restored = true;
		restoreTrackStop(track, originalStop);
	};
}

export function getArmedCaptureId(capture: ArmedNativeAudioCapture | null): string | null {
	return capture?.kind === 'capture' ? capture.captureId : null;
}

export function unsupportedPlatformAvailability(): NativeAudioAvailability {
	return {available: false, reason: 'unsupported-platform'};
}

export function shouldMixSelfWindowAudioIntoSystemCapture(_availability: NativeAudioAvailability): boolean {
	return false;
}

export function mixToStereoInterleaved(samples: Float32Array, channels: number): Float32Array {
	const frameCount = Math.floor(samples.length / channels);
	const out = new Float32Array(frameCount * 2);
	if (channels === 1) {
		for (let i = 0; i < frameCount; i++) {
			out[i * 2] = samples[i]!;
			out[i * 2 + 1] = samples[i]!;
		}
	} else {
		for (let i = 0; i < frameCount; i++) {
			let left = samples[i * channels]!;
			let right = samples[i * channels + 1]!;
			for (let ch = 2; ch < channels; ch++) {
				const s = samples[i * channels + ch]! * 0.5;
				if (ch % 2 === 0) {
					left += s;
				} else {
					right += s;
				}
			}
			out[i * 2] = Math.max(-1, Math.min(1, left));
			out[i * 2 + 1] = Math.max(-1, Math.min(1, right));
		}
	}
	return out;
}

export function resampleInterleavedStereo(samples: Float32Array, srcRate: number, dstRate: number): Float32Array {
	if (srcRate === dstRate) return samples;
	if (srcRate <= 0 || dstRate <= 0 || samples.length < 2) return new Float32Array(0);
	const srcFrames = Math.floor(samples.length / 2);
	const dstFrames = Math.round((srcFrames * dstRate) / srcRate);
	if (dstFrames === 0) return new Float32Array(0);
	const out = new Float32Array(dstFrames * 2);
	const ratio = srcRate / dstRate;
	for (let i = 0; i < dstFrames; i++) {
		const srcPos = i * ratio;
		const idx = Math.floor(srcPos);
		const frac = srcPos - idx;
		const idx0 = idx * 2;
		const idx1 = Math.min(idx + 1, srcFrames - 1) * 2;
		out[i * 2] = samples[idx0]! * (1 - frac) + samples[idx1]! * frac;
		out[i * 2 + 1] = samples[idx0 + 1]! * (1 - frac) + samples[idx1 + 1]! * frac;
	}
	return out;
}
